import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowIcon } from "../../assets/icons/svg/arrow-twoside-icon.svg";
import { ReactComponent as PenIcon } from "../../assets/icons/svg/pen-icon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/svg/download-icon.svg";
import { ReactComponent as ArrowReloadIcon } from "../../assets/icons/svg/arrow-reload-icon.svg";
import { ReactComponent as PencilEditIcon } from "../../assets/icons/svg/pencil-edit-icon.svg";
import { ReactComponent as PlayIcon } from "../../assets/icons/svg/play-icon.svg";
import { ReactComponent as PauseIcon } from "../../assets/icons/svg/pause-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/svg/delete-icon.svg";
import { ReactComponent as WifiLiveIcon } from "../../assets/icons/svg/wifi-live-icon.svg";
import { ReactComponent as WifiOfflineIcon } from "../../assets/icons/svg/wifi-offline-icon.svg";
import { Creative } from "../../types/creative.types";
import { useCreatives } from "../../context/CreativeContext";
import CustomButton from "./CommonButton";
import ItemListPopup from "./ItemListPopup";
import ToastContainer, { useToast } from "./Toast_Message";
import DeletePopup from "./DeletePopup";
import PreviewPopup from "./PreviewPopup";

interface CreativesCardProps {
  creative: Creative;
  onUpdate?: (updatedCreative: Creative) => void;
  onDelete?: (creativeId: string) => void;
  allCreatives?: Creative[];
}

const CreativesCard: React.FC<CreativesCardProps> = ({
  creative,
  onUpdate,
  onDelete,
  allCreatives = [],
}) => {
  // Use global methods for synchronization with ScheduledCreativesCard
  const { updateCreativeGlobally, deleteCreativeGlobally } = useCreatives();
  const navigate = useNavigate();
  const { showToast, toasts } = useToast();

  // Local state management
  const [imageError, setImageError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(creative.title);
  const [titleError, setTitleError] = useState("");
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [actionType, setActionType] = useState<"pause" | "delete">("delete");
  const [showPreview, setShowPreview] = useState(false);

  /**
   * Validate title for duplicates and empty input
   */
  const validateTitle = (title: string): string => {
    if (!title.trim()) {
      return "Title cannot be empty";
    }

    const creativesToCheck =
      allCreatives.length > 0 ? allCreatives : [creative];
    const isDuplicate = creativesToCheck.some(
      (c: Creative) =>
        c.id !== creative.id &&
        c.title.toLowerCase().trim() === title.toLowerCase().trim()
    );

    if (isDuplicate) {
      return "A creative with this title already exists";
    }

    return "";
  };

  /**
   * Handle title change with validation
   */
  const handleTitleChange = (newTitle: string) => {
    setEditedTitle(newTitle);
    setTitleError("");
  };

  /**
   * Toggles the play/pause state using global context
   * This will sync with ScheduledCreativesCard automatically
   */
  const handleTogglePlayPause = (): void => {
    if (creative.isPlaying) {
      // Show confirmation before pausing
      setActionType("pause");
      setShowActionConfirm(true);
    } else {
      // Directly activate without confirmation
      updateCreativeGlobally(creative.id, { isPlaying: true });
      const updatedCreative = { ...creative, isPlaying: true };
      onUpdate?.(updatedCreative);
      showToast("success", "", `Creative activated successfully!`);
    }
  };

  /**
   * Handles creative deletion with confirmation
   */
  const handleDeleteClick = (): void => {
    setActionType("delete");
    setShowActionConfirm(true);
  };

  /**
   * Handles the actual deletion
   * This will remove from both CreativesCard and ScheduledCreativesCard
   */
  const handleDelete = (): void => {
    deleteCreativeGlobally(creative.id);
    onDelete?.(creative.id);
    showToast("success", "", "Creative deleted successfully!");
  };

  /**
   * Handles download functionality
   */
  const handleDownload = (): void => {
    try {
      const link = document.createElement("a");
      link.href = creative.imageUrl;
      link.download = creative.title || "creative-image";

      if (
        creative.imageUrl.startsWith("data:") ||
        creative.imageUrl.startsWith("blob:")
      ) {
        link.setAttribute(
          "download",
          `${creative.title || "creative-image"}.png`
        );
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast("success", "", "Creative downloaded successfully!");
    } catch (error) {
      showToast("error", "", "Failed to download creative!");
      console.error("Download error:", error);
    }
  };

  /**
   * Handles image error
   */
  const handleImageError = (): void => {
    setImageError(true);
  };

  /**
   * Handles preview popup
   */
  const handlePreviewClick = (): void => {
    setShowPreview(true);
  };

  /**
   * Handles replace functionality - navigates to replace screen
   * Pass the current location as state so ReplaceCreativeScreen knows where to navigate back
   */
  const handleReplaceClick = (): void => {
    navigate(`/creatives/replace/${creative.id}`, {
      state: {
        from: "/creatives/list", // or wherever your creatives list is located
        creativeType: "regular",
      },
    });
  };

  /**
   * Handles edit functionality
   */
  const handleEditClick = (): void => {
    setIsEditing(true);
    setEditedTitle(creative.title);
    setTitleError("");
  };

  /**
   * Handles title update with validation
   * This will sync the title change with ScheduledCreativesCard automatically
   */
  const handleTitleUpdate = (): void => {
    const error = validateTitle(editedTitle);
    setTitleError(error);
    if (error) {
      return;
    }

    if (editedTitle !== creative.title) {
      // Update globally to sync with ScheduledCreativesCard
      updateCreativeGlobally(creative.id, { title: editedTitle });
      const updatedCreative = { ...creative, title: editedTitle };
      onUpdate?.(updatedCreative);
      showToast("success", "", "Title updated successfully!");
      setIsEditing(false);
    } else {
      setIsEditing(false);
    }
  };

  /**
   * Handles the confirmation popup actions
   */
  const handleConfirmAction = (): void => {
    if (actionType === "pause") {
      // Deactivate creative globally - this will sync with ScheduledCreativesCard
      updateCreativeGlobally(creative.id, { isPlaying: false });
      const updatedCreative = { ...creative, isPlaying: false };
      onUpdate?.(updatedCreative);
      showToast("success", "", "Creative deactivated successfully!");
    } else {
      // Delete creative
      handleDelete();
    }
    setShowActionConfirm(false);
  };

  /**
   * Function to get file format from imageUrl or use stored fileFormat
   */
  const getDisplayFormat = () => {
    // Handle base64 data URI format
    if (creative.imageUrl?.startsWith("data:")) {
      const match = creative.imageUrl.match(/^data:(image\/[a-zA-Z]+);base64,/);
      return match ? match[1] : "unknown";
    }

    // Fallback: Check fileFormat field
    if (creative.fileFormat) {
      return `image/${creative.fileFormat.toLowerCase()}`;
    }

    // Extract from imageUrl extension (e.g., .png, .jpg)
    if (creative.imageUrl) {
      const urlParts = creative.imageUrl.split(".");
      const extension = urlParts[urlParts.length - 1]
        .split("?")[0]
        .toLowerCase();
      return `image/${extension}`;
    }

    return creative.format || "unknown";
  };

  /**
   * Get action button configuration based on creative state
   * Updated to always show replace button instead of delete for live creatives
   */
  const getActionButtonConfig = () => {
    if (creative.isPlaying) {
      return {
        icon: (
          <ArrowReloadIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Replace Creative"
          />
        ),
        onClick: handleReplaceClick,
      };
    } else {
      return {
        icon: (
          <DeleteIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-red-500 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Delete Creative"
          />
        ),
        onClick: handleDeleteClick,
      };
    }
  };

  // Different background options - you can choose which one to use
  const backgroundOptions = {
    gradient: "bg-gradient-to-br from-blue-50 to-indigo-100",
    solid: "bg-gray-100",
    brandGradient: "bg-gradient-to-br from-[#0BA6C7]/10 to-[#0BA6C7]/20",
    warmGradient: "bg-gradient-to-br from-orange-50 to-pink-50",
    coolGradient: "bg-gradient-to-br from-blue-50 to-purple-50",
  };

  const actionButtonConfig = getActionButtonConfig();

  return (
    <>
      <div className="w-full h-[310px] xl:w-full xl:h-[310px] 2xl:w-full 2xl:h-[350px] 3xl:w-full 3xl:h-[340px] 4xl:w-full 4xl:h-[340px] 5xl:w-full 5xl:h-[370px] bg-white rounded-[16px] overflow-hidden shadow-md hover:shadow-lg transition-shadow relative">
        {/* Image Section - Full image display with background */}
        <div
          className={`relative w-full h-[160px] 2xl:h-[190px] 3xl:h-[190px] 4xl:h-[180px] 5xl:h-[210px] ${backgroundOptions.brandGradient} rounded-t-[16px] flex items-center justify-center overflow-hidden`}
        >
          {!imageError ? (
            <img
              src={creative.imageUrl}
              alt="Creative Thumbnail"
              className="max-w-full max-h-full object-contain transition-opacity duration-300"
              onError={handleImageError}
              style={{
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
              }}
            />
          ) : (
            // Fallback content when image fails to load
            <div className="flex flex-col items-center justify-center text-gray-400">
              <svg
                className="w-12 h-12 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xs">Image not available</span>
            </div>
          )}

          <div className="absolute top-3 right-3 w-8 h-8 p-1 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-sm backdrop-blur-sm">
            <ArrowIcon
              width={16}
              height={16}
              className="cursor-pointer hover:scale-110 transition-transform"
              onClick={handlePreviewClick}
            />
          </div>
        </div>

        {/* Content Section - Compact */}
        <div className="px-4 py-3 flex flex-col">
          {/* Status Tag - Smaller */}
          <div
            className={`flex items-center gap-1 text-xs font-medium h-5 px-2 py-1 rounded-md w-fit ${
              creative.isPlaying
                ? "bg-[#DFEDE5] text-[#0C6630]"
                : "bg-[#EDDFDF] text-[#DB2A2A]"
            }`}
          >
            {creative.isPlaying ? (
              <>
                <WifiLiveIcon width={12} height={12} />
                Live
              </>
            ) : (
              <>
                <WifiOfflineIcon width={12} height={12} />
                Offline
              </>
            )}
          </div>

          {/* Title - Compact with truncation */}
          <div className="text-black text-base 2xl:text-lg font-semibold flex items-center gap-2 mt-2 mb-1">
            <span className="" title={creative.title}>
              {creative.title}
            </span>
            <PenIcon
              width={14}
              height={14}
              className="flex-shrink-0 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={handleEditClick}
            />
          </div>

          {/* Sub Info - Updated to show file format without hover effect */}
          <div className="text-black text-xs 2xl:text-sm font-normal truncate">
            <span className="text-[#3C3C3C] mr-2">Created on:</span>
            <span className="text-[#3C3C3C]">{creative.createdOn}</span>
            <span className="mx-2">|</span>
            <span className="text-[#3C3C3C]">{getDisplayFormat()}</span>
          </div>
        </div>

        {/* Footer Section - Compact */}
        <div className="absolute bottom-0 left-0 right-0 h-14 flex justify-between items-center border-t border-gray-200 px-4 bg-white">
          {/* Download Button */}
          <DownloadIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Download"
            onClick={handleDownload}
          />

          {/* Edit Button */}
          <PencilEditIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Edit"
            onClick={handleEditClick}
          />

          {/* Conditional Action Button - Replace or Delete */}
          <div onClick={actionButtonConfig.onClick}>
            {actionButtonConfig.icon}
          </div>

          {/* Play/Pause Toggle Button */}
          <div onClick={handleTogglePlayPause} className="cursor-pointer">
            {creative.isPlaying ? (
              <PauseIcon
                className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-red-500 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
                title="Pause"
              />
            ) : (
              <PlayIcon
                className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-green-500 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
                title="Play"
              />
            )}
          </div>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} />

      {/* Edit Popup */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-40">
          <ItemListPopup
            title="Edit Creative"
            onClose={() => {
              setIsEditing(false);
              setEditedTitle(creative.title);
              setTitleError("");
            }}
          >
            <div className="flex flex-col h-full max-h-[90vh]">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm font-medium text-gray-700">
                  Dimension
                </span>
                <span className="text-sm text-gray-600">
                  {creative.orientation === "landscape"
                    ? "1920 × 1080"
                    : "1080 × 1920"}
                </span>
              </div>

              <div className="mb-4">
                <div className="w-full h-48 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden">
                  {!imageError ? (
                    <img
                      src={creative.imageUrl}
                      alt={creative.title || "Creative Preview"}
                      className="max-w-full max-h-full object-contain"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <svg
                        className="w-12 h-12 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm">Image not available</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Creative name
                </label>
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className={`w-full border rounded-[8px] px-3 py-2 ${
                    titleError
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                  } focus:outline-none focus:ring-2`}
                  maxLength={32}
                  placeholder="Enter creative name"
                />
                {titleError && (
                  <p className="mt-1 text-sm text-red-600">{titleError}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-auto">
                <hr className="border-t border-gray-200 mt-1" />
                <div className="flex justify-end gap-3 pt-4">
                  <CustomButton
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedTitle(creative.title);
                      setTitleError("");
                    }}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    isActive={
                      editedTitle !== creative.title &&
                      !titleError &&
                      !!editedTitle.trim()
                    }
                    onClick={handleTitleUpdate}
                    disabled={!!titleError || !editedTitle.trim()}
                  >
                    Apply changes
                  </CustomButton>
                </div>
              </div>
            </div>
          </ItemListPopup>
        </div>
      )}

      {/* Action Confirmation Popup */}
      {showActionConfirm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <DeletePopup
            title={
              actionType === "pause" ? "Deactivate Creative" : "Delete Creative"
            }
            message={
              actionType === "pause"
                ? "Are you sure you want to deactivate this creative? It will stop playing on all screens."
                : "Are you sure you want to delete this creative? This action cannot be undone."
            }
            onCancel={() => setShowActionConfirm(false)}
            onConfirm={handleConfirmAction}
          />
        </div>
      )}

      {/* Preview Popup */}
      {showPreview && (
        <PreviewPopup
          creative={creative}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};

export default CreativesCard;
